import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-regular-svg-icons";
import { useAuth } from "../../context/AuthProvider";
import BusinessLayoutV2 from "../../components/BusinessLayoutV2";
import { getBusinessById, updateBusinessData } from "../../services/firestoreService";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

const BusinessInformationV2 = () => {
    const { userData } = useAuth();
    const [formData, setFormData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [resetMsg, setResetMsg] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [onboardingStatus, setOnboardingStatus] = useState("");
    const auth = getAuth();

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.businessID) return;
            setIsLoading(true);
            const businessData = await getBusinessById(userData.businessID);
            if (businessData) {
                setOnboardingStatus(businessData.onboardingStatus || "");
                setFormData({
                    legalName: businessData.legalName || "",
                    displayName: businessData.displayName || "",
                    repFirstName: businessData.repFirstName || "",
                    repLastName: businessData.repLastName || "",
                    repEmail: businessData.repEmail || "",
                    repPhone: businessData.repPhone || "",
                    address: businessData.address || "",
                    tipAmountConfig: businessData.tipAmountConfig || [2, 4, 8, 10],
                    defaultTipAmount: businessData.defaultTipAmount || "",
                    subscriptionPlan: businessData.subscriptionPlan || "Basic",
                    googlePlaceID: businessData.googlePlaceID || "",
                    teamImgUrl: businessData.teamImgUrl || "",
                });
            }
            setIsLoading(false);
        };
        fetchData();
    }, [userData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTipAmountChange = (index, value) => {
        const parsed = parseInt(value, 10);
        const newConfig = [...formData.tipAmountConfig];
        newConfig[index] = !Number.isNaN(parsed) && parsed >= 1 ? parsed : 1;
        setFormData((prev) => ({ ...prev, tipAmountConfig: newConfig }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const storage = getStorage();
        const storageRef = ref(storage, `businessTeamImages/${userData.businessID}_${file.name}`);
        try {
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            setFormData((prev) => ({ ...prev, teamImgUrl: url }));
        } catch (error) {
            console.error("Error uploading image:", error);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        await updateBusinessData(userData.businessID, formData);
        setSaving(false);
        alert("Business information updated.");
    };

    const handlePasswordReset = async () => {
        setResetMsg("");
        const user = auth.currentUser;
        if (!user?.email) {
            setResetMsg("Error: No email associated with your account.");
            return;
        }
        try {
            await sendPasswordResetEmail(auth, user.email);
            setResetMsg("Password reset email sent! Check your inbox.");
        } catch (error) {
            setResetMsg("Error sending reset email: " + error.message);
        }
    };

    const canEditCore = ["SETUP", "CHANGES_REQUESTED"].includes(onboardingStatus);

    return (
        <BusinessLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
                .biz-info-v2 { width: 100%; padding: 32px 0 80px; font-family: 'DM Sans', -apple-system, sans-serif; }
                .biz-info-v2 .title { font-size: 28px; font-weight: 700; letter-spacing: -0.5px; margin-bottom: 16px; }
                .biz-info-v2 .surface { width: min(100%, 560px); background: #fff; border: 1px solid #e8e8ee; border-radius: 18px; padding: 24px; }
                .biz-info-v2 .loading-wrap { display:flex; align-items:center; justify-content:center; min-height:140px; background:#fff; border:1px solid #e8e8ee; border-radius:14px; width:min(100%,560px); }
                .biz-info-v2 .spinner { width:28px; height:28px; border:3px solid #dbe8e2; border-top-color:#0d7c5f; border-radius:9999px; animation: spin .8s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>

            <div className="biz-info-v2">
                <h1 className="title">Business Information</h1>

                {isLoading || !formData ? (
                    <div className="loading-wrap"><div className="spinner" /></div>
                ) : (
                    <div className="surface">
                        <div className="space-y-5 max-w-lg mb-16">
                            <div className="flex flex-col items-center mb-8">
                                <img src={formData.teamImgUrl || "https://i.pravatar.cc/120"} alt="Team" className="w-32 h-32 rounded-full border object-cover" />
                                <label htmlFor="avatar-upload" className="mt-4 px-3 py-1 text-sm border rounded-full text-gray-700 cursor-pointer hover:bg-gray-100">
                                    Change picture <FontAwesomeIcon icon={faEdit} />
                                </label>
                                <input id="avatar-upload" type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                <p className="mt-3 text-sm text-gray-500 text-center">
                                    This picture may be shown on your public Tiply page.
                                </p>
                            </div>

                            {[
                                { label: "Business Legal Name", name: "legalName", disabled: !canEditCore },
                                { label: "Business Display Name", name: "displayName", disabled: !canEditCore },
                                { label: "Representative First Name", name: "repFirstName", disabled: !canEditCore },
                                { label: "Representative Last Name", name: "repLastName", disabled: !canEditCore },
                                { label: "Representative Email", name: "repEmail", disabled: true },
                                { label: "Phone Number", name: "repPhone", disabled: !canEditCore },
                                { label: "Business Address", name: "address", disabled: !canEditCore },
                            ].map(({ label, name, disabled }) => (
                                <div key={name}>
                                    <label className="block text-sm font-medium text-gray-700">{label}</label>
                                    <input name={name} value={formData[name]} onChange={handleChange} className="w-full mt-1 p-2 border rounded-md" disabled={disabled} />
                                </div>
                            ))}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Subscription Plan</label>
                                <input name="subscriptionPlan" value={formData.subscriptionPlan} readOnly className="w-full mt-1 p-2 border rounded-md bg-gray-100 cursor-not-allowed" />
                            </div>

                            {formData.subscriptionPlan === "pro" && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Google Place ID</label>
                                    <input name="googlePlaceID" value={formData.googlePlaceID || ""} onChange={handleChange} className="w-full mt-1 p-2 border rounded-md" />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tip Amount Configuration (€)</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {formData.tipAmountConfig.map((amount, idx) => (
                                        <input key={idx} type="number" step="1" min="1" value={amount} onChange={(e) => handleTipAmountChange(idx, e.target.value)} className="p-2 border rounded-md text-center" />
                                    ))}
                                </div>
                            </div>

                            <div className="mt-4 max-w-lg">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Default Tip Amount (€)</label>
                                <input
                                    type="number"
                                    step="1"
                                    min="1"
                                    value={formData.defaultTipAmount}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        setFormData((prev) => ({ ...prev, defaultTipAmount: Number.isNaN(value) || value < 1 ? 1 : value }));
                                    }}
                                    className="p-2 w-full border rounded-md text-center"
                                />
                            </div>

                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className={`w-full mt-6 py-2 font-semibold rounded-md transition ${
                                    saving ? "bg-gray-400 text-white cursor-not-allowed" : "bg-black text-white hover:bg-neutral-800"
                                }`}
                            >
                                {saving ? "Saving..." : "Save Changes"}
                            </button>

                            <button className="w-full mt-4 py-2 border border-gray-600 text-gray-700 font-semibold rounded-md" onClick={handlePasswordReset}>
                                Reset Password
                            </button>
                            {resetMsg && <p className="text-sm mt-2 text-blue-700">{resetMsg}</p>}

                            <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 rounded-md shadow-sm">
                                {canEditCore
                                    ? "You can update core details until the activation request is submitted."
                                    : "Please contact team@tiply.ee if core business details need to change."}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </BusinessLayoutV2>
    );
};

export default BusinessInformationV2;
